/*
 * Fig & Bloom - enforce the premium greeting-card message limit on the cart page.
 *
 * The Order Update Dashboard app's message builder (an app block, so not in this
 * repo) reads the limit from its premium_char_limit setting
 * (window.messageBuilderSettings.premiumCharLimit) but with a premium card in the
 * cart it only displays it: typing and pasting run past it, the counter just reads
 * "1300 / 1000", checkout stays open and the whole message is saved as the order
 * note. Its popup-cart.js also trims a note to a hard-coded 1200 without saying so.
 *
 * With a premium card in the cart this script:
 *   - caps typing and pasting at the limit in the card message field. A paste that
 *     would run past it goes in only up to the room left, and a note says so;
 *   - marks a message that is already over (pasted earlier, written by the AI,
 *     or saved from before) with "1,300 / 1,000: please shorten your message",
 *     aria-invalid and error styling, and blocks checkout (the cart checkout
 *     button, the form submit and the app's popup checkout paths) until it is
 *     shortened. Nothing a customer wrote is ever trimmed.
 * Without a premium card the free limit (freeCharLimit, 200) is the app's to
 * show, as before: it lets the message run on and offers a premium card, so
 * typing is not capped here. But its checkout gate races (its counter and
 * button follow a state that is updated a frame late, so "201 / 200" could
 * leave Secure Checkout enabled), so an over-limit message blocks checkout here
 * too, from the text the customer can see, for either limit.
 *
 * The limit always comes from the setting; there is no number here. The app
 * re-renders its states and re-creates its handlers, so everything is delegated
 * (window capture, which runs before the app's document-capture checkout
 * handler) and re-checked on input, on DOM changes and on a short interval.
 *
 * Tests: tests/fb-card-message-limit.test.js
 */
(function (w, d) {
  if (w.fbCardMessageLimit) return;

  var FIELDS = ['messageTextarea', 'resultTextarea', 'overlimitTextarea', 'successTextarea'];
  var CHECKOUT = '#checkout, [name="checkout"], [id^="free-shipping-checkout-btn-"], [id^="free-shipping-no-thanks-btn-"]';
  var GUARDED = ['saveOrderNotes', 'proceedFromFreeShippingPopup', 'skipFreeShippingPopup'];
  var OURS = 'data-fb-card-limit';
  var inserting = false;

  function settings() { return w.messageBuilderSettings || {}; }
  function num(v) { var n = parseInt(v, 10); return n > 0 ? n : 0; }
  function fmt(n) { try { return Number(n).toLocaleString('en-AU'); } catch (e) { return String(n); } }

  function isPremium() {
    var mb = w.messageBuilder;
    if (mb && typeof mb.hasPremiumCard === 'boolean') return mb.hasPremiumCard;
    if (typeof w.checkPremiumSelection === 'function') {
      try { return !!w.checkPremiumSelection(w.UpdatedCartItems || []); } catch (e) {}
    }
    return false;
  }
  /* The cap on typing and pasting: the premium setting, only with a premium card. */
  function limit() {
    if (!isPremium()) return 0;
    return num(settings().premiumCharLimit);
  }
  /* The limit checkout is held to: premium with a premium card, else the free one
     (freeCharLimit, or 200 as the app falls back). */
  function currentLimit() {
    return isPremium() ? num(settings().premiumCharLimit) : (num(settings().freeCharLimit) || 200);
  }

  function fields() {
    var out = [];
    for (var i = 0; i < FIELDS.length; i++) {
      var f = d.getElementById(FIELDS[i]);
      if (f) out.push(f);
    }
    return out;
  }
  function isField(el) { return !!el && el.tagName === 'TEXTAREA' && FIELDS.indexOf(el.id) !== -1; }
  function visible(el) { return !!el && el.offsetParent !== null; }
  function activeField() {
    var all = fields();
    for (var i = 0; i < all.length; i++) if (visible(all[i])) return all[i];
    return null;
  }
  /* The longer of what the app will save (MessageBuilder.getCurrentMessage) and
     what the customer can see: the app switches state before it copies the text
     across (a frame later), so either can lag the other for a moment. */
  function messageLength() {
    var n = -1;
    var mb = w.messageBuilder;
    if (mb && typeof mb.getCurrentMessage === 'function') {
      try { var m = mb.getCurrentMessage(); if (typeof m === 'string') n = m.length; } catch (e) {}
    }
    var f = activeField();
    if (f) n = Math.max(n, (f.value || '').length);
    if (n < 0) {
      var hidden = d.getElementById('card-message-box');
      n = hidden ? (hidden.value || '').length : 0;
    }
    return n;
  }
  function overBy() {
    var lim = currentLimit();
    if (!lim) return 0;
    var n = messageLength() - lim;
    return n > 0 ? n : 0;
  }
  function overText(lim, over) {
    var t = fmt(lim + over) + ' / ' + fmt(lim) + ': please shorten your message';
    var premium = num(settings().premiumCharLimit);
    if (!isPremium() && premium > lim) t += ', or add a premium card for up to ' + fmt(premium) + ' characters';
    return t;
  }

  /* ---- notes ---------------------------------------------------------------- */
  function noteFor(field, create) {
    var id = field.id + '-fb-limit-note';
    var note = d.getElementById(id);
    if (!note && create) {
      note = d.createElement('p');
      note.id = id;
      note.className = 'fb-card-limit-note';
      note.setAttribute('role', 'status');
      note.setAttribute('aria-live', 'polite');
      note.setAttribute('aria-atomic', 'true');
      note.hidden = true;
      field.parentNode.insertBefore(note, field.nextSibling);
      var by = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (by.indexOf(id) === -1) { by.push(id); field.setAttribute('aria-describedby', by.join(' ')); }
    }
    return note;
  }
  function showNote(field, kind, text) {
    var note = noteFor(field, true);
    if (note.textContent !== text) note.textContent = text;
    /* Only touch what changed, so our own MutationObserver isn't woken by it. */
    if (note.getAttribute('data-kind') !== kind) note.setAttribute('data-kind', kind);
    var cls = 'fb-card-limit-note' + (kind === 'over' ? ' fb-card-limit-note--error' : ' fb-card-limit-note--info');
    if (note.className !== cls) note.className = cls;
    if (note.hidden) note.hidden = false;
  }
  function hideNote(field, kinds) {
    var note = noteFor(field, false);
    if (!note || note.hidden) return;
    if (kinds && kinds.indexOf(note.getAttribute('data-kind')) === -1) return;
    note.hidden = true;
    note.textContent = '';
    note.removeAttribute('data-kind');
  }
  function setInvalid(field, on) {
    if (on) {
      if (field.getAttribute('aria-invalid') !== 'true') field.setAttribute('aria-invalid', 'true');
      if (!field.classList.contains('fb-card-limit--over')) field.classList.add('fb-card-limit--over');
    } else if (field.classList.contains('fb-card-limit--over')) {
      field.removeAttribute('aria-invalid');
      field.classList.remove('fb-card-limit--over');
    }
  }

  function checkoutNote(btn, create) {
    var note = d.getElementById('fb-card-limit-checkout-note');
    if (!note && create && btn && btn.parentNode) {
      note = d.createElement('p');
      note.id = 'fb-card-limit-checkout-note';
      note.className = 'fb-card-limit-note fb-card-limit-note--error';
      note.setAttribute('role', 'status');
      note.setAttribute('aria-live', 'polite');
      note.setAttribute('aria-atomic', 'true');
      note.hidden = true;
      btn.parentNode.insertBefore(note, btn);
    }
    return note;
  }
  function setCheckoutBlocked(over, lim) {
    var btn = d.getElementById('checkout');
    if (!btn) return;
    if (over) {
      if (!btn.hasAttribute('data-fb-card-limit-blocked')) btn.setAttribute('data-fb-card-limit-blocked', '');
      if (btn.getAttribute('aria-disabled') !== 'true') btn.setAttribute('aria-disabled', 'true');
      if (!btn.disabled) btn.disabled = true;
      var note = checkoutNote(btn, true);
      if (note) {
        var text = 'Your card message is ' + fmt(over) + (over === 1 ? ' character' : ' characters') +
          ' over the ' + fmt(lim) + ' limit. Shorten it to check out.';
        if (note.textContent !== text) note.textContent = text;
        if (note.hidden) note.hidden = false;
      }
      if (btn.id) {
        var by = (btn.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
        if (by.indexOf('fb-card-limit-checkout-note') === -1) { by.push('fb-card-limit-checkout-note'); btn.setAttribute('aria-describedby', by.join(' ')); }
      }
    } else if (btn.hasAttribute('data-fb-card-limit-blocked')) {
      btn.removeAttribute('data-fb-card-limit-blocked');
      btn.removeAttribute('aria-disabled');
      /* Hand the button back to the app's own rule: open unless the delivery
         acknowledgement is there and unticked. */
      var ack = d.getElementById('cart-acknowledge-checkbox');
      btn.disabled = !!(ack && !ack.checked);
      var n = checkoutNote(btn, false);
      if (n) { n.hidden = true; n.textContent = ''; }
    }
  }

  /* ---- the check ------------------------------------------------------------- */
  function sync() {
    var cap = limit();          /* typing/paste cap: premium only */
    var lim = currentLimit();   /* checkout limit: premium or free */
    var all = fields();
    var active = activeField();
    var over = overBy();
    for (var j = 0; j < all.length; j++) {
      var f = all[j];
      if (cap) {
        /* Native cap for typing, IME and autocorrect. A value already over it can
           still be shortened. Paste and drop are handled below, with a note. */
        if (f.getAttribute('maxlength') !== String(cap)) { f.setAttribute('maxlength', String(cap)); f.setAttribute(OURS, ''); }
      } else if (f.getAttribute(OURS) !== null) {
        f.removeAttribute('maxlength'); f.removeAttribute(OURS);
      }
      var fOver = f === active ? over : 0;
      if (fOver) {
        setInvalid(f, true);
        showNote(f, 'over', overText(lim, fOver));
      } else {
        setInvalid(f, false);
        hideNote(f, ['over']);
        if (!cap || (f.value || '').length < cap) hideNote(f, ['max', 'paste']);
      }
    }
    setCheckoutBlocked(over, lim);
    watchButton();
    fixOverCount();
    return over;
  }

  /* The app's upsell panel reads "Your message needs <strong id=overCount>N</strong>
     more characters than our standard 200-character limit", but its counter code
     also writes "200 / 200" into that same element (when it updates the overlimit
     state from text it has not copied across yet), so the panel read "needs
     200 / 200 more characters". Keep it a number: how far over the free limit
     the message is. */
  var overCountObserver = null, overCountEl = null;
  function fixOverCount() {
    var el = d.getElementById('overCount');
    if (!el) return;
    if (isPremium()) return;
    var free = num(settings().freeCharLimit) || 200;
    var want = String(Math.max(0, messageLength() - free));
    if (el.textContent !== want && !/^\d+$/.test(el.textContent || '')) el.textContent = want;
    else if (/^\d+$/.test(el.textContent || '') && el.textContent !== want && messageLength() > free) el.textContent = want;
    if (el !== overCountEl && w.MutationObserver) {
      if (overCountObserver) overCountObserver.disconnect();
      overCountEl = el;
      overCountObserver = new w.MutationObserver(function () { fixOverCount(); });
      try { overCountObserver.observe(el, { childList: true, characterData: true, subtree: true }); } catch (e) {}
    }
  }

  /* The app re-enables the checkout button on its own (a frame after an input, on
     the acknowledgement, after a cart update). While the message is over, put it
     straight back, so it never shows as enabled. */
  var watched = null, buttonObserver = null;
  function watchButton() {
    var btn = d.getElementById('checkout');
    if (!btn || btn === watched || !w.MutationObserver) return;
    if (buttonObserver) buttonObserver.disconnect();
    watched = btn;
    buttonObserver = new w.MutationObserver(function () {
      if (btn.hasAttribute('data-fb-card-limit-blocked') && !btn.disabled && overBy()) btn.disabled = true;
    });
    try { buttonObserver.observe(btn, { attributes: true, attributeFilter: ['disabled'] }); } catch (e) {}
  }

  /* Checkout was tried with a message over the limit: say why, where it can be fixed. */
  function refuse() {
    var lim = currentLimit();
    if (typeof w.closePremiumPopup === 'function') { try { w.closePremiumPopup(); } catch (e) {} }
    var overlays = d.querySelectorAll('.upsell-popup-overlay');
    for (var i = 0; i < overlays.length; i++) overlays[i].style.display = 'none';
    if (typeof w.setCheckoutBusy === 'function') { try { w.setCheckoutBusy(false); } catch (e) {} }
    sync();
    var f = activeField();
    if (f) {
      showNote(f, 'over', overText(lim, overBy()));
      try { f.scrollIntoView({ block: 'center' }); } catch (e) {}
      try { f.focus({ preventScroll: true }); } catch (e) { f.focus(); }
    }
  }

  /* ---- input ----------------------------------------------------------------- */
  function cut(text, room) {
    var s = text.slice(0, room);
    var c = s.charCodeAt(s.length - 1);
    if (c >= 0xd800 && c <= 0xdbff) s = s.slice(0, -1); /* don't split an emoji */
    return s;
  }
  function insert(field, text) {
    var start = field.selectionStart, end = field.selectionEnd;
    if (typeof start !== 'number') { start = end = field.value.length; }
    inserting = true;
    try {
      field.setRangeText(text, start, end, 'end');
      field.dispatchEvent(new Event('input', { bubbles: true }));
    } finally { inserting = false; }
  }
  function room(field, lim) {
    var start = field.selectionStart, end = field.selectionEnd;
    var selected = typeof start === 'number' ? end - start : 0;
    return lim - (field.value.length - selected);
  }
  function onInsertFrom(e, text) {
    var f = e.target;
    if (!isField(f)) return;
    var lim = limit();
    if (!lim || !text) return;
    var r = room(f, lim);
    if (text.length <= r) return;
    e.preventDefault();
    var part = r > 0 ? cut(text, r) : '';
    if (part) insert(f, part);
    var left = text.length - part.length;
    showNote(f, 'paste', part
      ? 'Only the first ' + fmt(part.length) + ' characters were added: your card message can be up to ' + fmt(lim) +
        ' characters, so the last ' + fmt(left) + ' were left out.'
      : 'Nothing was added: your card message is already at the ' + fmt(lim) + ' character limit.');
    sync();
  }
  d.addEventListener('paste', function (e) {
    var cd = e.clipboardData || w.clipboardData;
    onInsertFrom(e, cd ? cd.getData('text/plain') || cd.getData('text') : '');
  }, true);
  d.addEventListener('drop', function (e) {
    onInsertFrom(e, e.dataTransfer ? e.dataTransfer.getData('text/plain') : '');
  }, true);
  d.addEventListener('beforeinput', function (e) {
    var f = e.target;
    if (!isField(f)) return;
    var lim = limit();
    if (!lim) return;
    var type = e.inputType || '';
    if (type.indexOf('insert') !== 0 || type === 'insertFromPaste' || type === 'insertFromDrop' || type === 'insertCompositionText') return;
    var data = e.data != null ? e.data : (type === 'insertLineBreak' || type === 'insertParagraph' ? '\n' : '');
    if (!data) return;
    var r = room(f, lim);
    if (data.length <= r) return;
    e.preventDefault();
    if (r > 0) insert(f, cut(data, r));
    showNote(f, 'max', 'You\u2019ve reached the ' + fmt(lim) + ' character limit for your card message.');
  }, true);
  d.addEventListener('input', function (e) {
    if (!isField(e.target)) return;
    if (!inserting && (e.target.value || '').length < limit()) hideNote(e.target, ['paste', 'max']);
    sync();
    /* and again once the app's own frame-late update has run */
    if (w.requestAnimationFrame) w.requestAnimationFrame(function () { w.setTimeout(sync, 0); });
  }, true);

  /* ---- checkout -------------------------------------------------------------- */
  w.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest(CHECKOUT) : null;
    if (!t || !sync()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    refuse();
  }, true);
  w.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart"], form[action*="/checkout"]')) return;
    if (!sync()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    refuse();
  }, true);
  /* The app's own note savers: the cart checkout, its premium/upsell popup and the
     free-shipping popup (whose hard-coded 1200 trim must never see an over-limit
     message). Wrapped wherever they are defined, so every path is covered even
     if a click is not. */
  function guard() {
    for (var i = 0; i < GUARDED.length; i++) {
      var name = GUARDED[i];
      var fn = w[name];
      if (typeof fn !== 'function' || fn.fbCardLimitGuard) continue;
      w[name] = (function (orig, saver) {
        var wrapped = function () {
          /* Refused: the note says why. The app's save never settles, so it does not
             go on to checkout or report a failed save. */
          if (sync()) { refuse(); return saver ? new Promise(function () {}) : Promise.resolve(); }
          return orig.apply(this, arguments);
        };
        wrapped.fbCardLimitGuard = true;
        return wrapped;
      })(fn, name === 'saveOrderNotes');
    }
  }

  function start() {
    guard();
    sync();
    if (w.MutationObserver) {
      var root = d.getElementById('state-default');
      root = (root && root.closest && root.closest('.cart-app-block')) || d.body;
      try {
        new w.MutationObserver(function () { sync(); })
          .observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
      } catch (e) {}
    }
    /* Programmatic value changes (AI text, restored notes, cart updates) fire no event. */
    w.setInterval(function () { if (!d.hidden) { guard(); sync(); } }, 500);
  }
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', start);
  else start();
  if (w.addEventListener) w.addEventListener('load', function () { guard(); sync(); });

  w.fbCardMessageLimit = { limit: limit, currentLimit: currentLimit, isPremium: isPremium, sync: sync, overBy: overBy, messageLength: messageLength };
})(window, document);
